import { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { Toaster } from '@/components/ui/sonner';
import { RequireAuth, RequireRole, RequireCompleteProfile, RoleRouter } from '@/components/shared/RoleGuard';
import PageSkeleton from '@/components/shared/PageSkeleton';

const AccessDeniedPage   = lazy(() => import('@/app/AccessDeniedPage'));
const PendingApprovalPage = lazy(() => import('@/app/PendingApprovalPage'));

const LoginPage        = lazy(() => import('@/app/(auth)/LoginPage'));
const SignupPage       = lazy(() => import('@/app/(auth)/SignupPage'));
const StaffDashboard   = lazy(() => import('@/app/(staff)/StaffDashboard'));
const PortalDashboard  = lazy(() => import('@/app/(subcontractor)/PortalDashboard'));

// Portal pages
const PortalProfilePage   = lazy(() => import('@/app/(subcontractor)/PortalProfilePage'));
const CompleteProfilePage = lazy(() => import('@/app/(subcontractor)/CompleteProfilePage'));

// Order pages — portal
const OrdersListPage   = lazy(() => import('@/app/(subcontractor)/orders/OrdersListPage'));
const OrderFormPage    = lazy(() => import('@/app/(subcontractor)/orders/OrderFormPage'));
const OrderDetailPage  = lazy(() => import('@/app/(subcontractor)/orders/OrderDetailPage'));
const CabinetOrderPage = lazy(() => import('@/app/(subcontractor)/orders/CabinetOrderPage'));

// Order pages — staff
const StaffOrdersPage      = lazy(() => import('@/app/(staff)/orders/StaffOrdersPage'));
const StaffOrderDetailPage = lazy(() => import('@/app/(staff)/orders/StaffOrderDetailPage'));
const StaffSettingsPage    = lazy(() => import('@/app/(staff)/StaffSettingsPage'));
const StaffUsersPage       = lazy(() => import('@/app/(staff)/users/StaffUsersPage'));

// CRM pages — staff
const StaffCrmClientsPage      = lazy(() => import('@/app/(staff)/crm/StaffCrmClientsPage'));
const StaffCrmClientDetailPage = lazy(() => import('@/app/(staff)/crm/StaffCrmClientDetailPage'));

// Catalog pages — staff
const CatalogFinishesPage      = lazy(() => import('@/app/(staff)/catalog/CatalogFinishesPage'));
const CatalogLookupsPage       = lazy(() => import('@/app/(staff)/catalog/CatalogLookupsPage'));
const CatalogTemplatesPage     = lazy(() => import('@/app/(staff)/catalog/CatalogTemplatesPage'));
const CatalogTemplateEditorPage = lazy(() => import('@/app/(staff)/catalog/CatalogTemplateEditorPage'));
const CatalogNewCabinetTypePage = lazy(() => import('@/app/(staff)/catalog/CatalogNewCabinetTypePage'));
const CatalogStandardCabinetsPage = lazy(() => import('@/app/(staff)/catalog/CatalogStandardCabinetsPage'));
const CatalogStandardCabinetEditorPage = lazy(() => import('@/app/(staff)/catalog/CatalogStandardCabinetEditorPage'));
const CatalogConfigsPage       = lazy(() => import('@/app/(staff)/catalog/CatalogConfigsPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

const STAFF_ONLY      = ['staff'] as const;
const CUSTOMER_ROLES  = ['subcontractor', 'home_owner', 'contractor', 'cabinet_shop', 'designer'] as const;

function StaffRoutes() {
  return (
    <Routes>
      <Route index element={<StaffDashboard />} />
      <Route path="orders" element={<StaffOrdersPage />} />
      <Route path="orders/:id" element={<StaffOrderDetailPage />} />
      <Route path="crm" element={<StaffCrmClientsPage />} />
      <Route path="crm/:id" element={<StaffCrmClientDetailPage />} />
      <Route path="catalog" element={<Navigate to="/staff/catalog/finishes" replace />} />
      <Route path="catalog/finishes" element={<CatalogFinishesPage />} />
      <Route path="catalog/lookups" element={<CatalogLookupsPage />} />
      <Route path="catalog/templates/new-type" element={<CatalogNewCabinetTypePage />} />
      <Route path="catalog/templates" element={<CatalogTemplatesPage />} />
      <Route path="catalog/templates/:id" element={<CatalogTemplateEditorPage />} />
      <Route path="catalog/standard-cabinets" element={<CatalogStandardCabinetsPage />} />
      <Route path="catalog/standard-cabinets/new" element={<CatalogStandardCabinetEditorPage />} />
      <Route path="catalog/standard-cabinets/:id" element={<CatalogStandardCabinetEditorPage />} />
      <Route path="catalog/configs" element={<CatalogConfigsPage />} />
      <Route path="users" element={<StaffUsersPage />} />
      <Route path="settings" element={<StaffSettingsPage />} />
    </Routes>
  );
}

function PortalRoutes() {
  return (
    <Routes>
      {/* Accessible before profile is complete */}
      <Route path="complete-profile" element={<CompleteProfilePage />} />
      {/* All other portal routes require a complete profile */}
      <Route element={<RequireCompleteProfile />}>
        <Route index element={<PortalDashboard />} />
        <Route path="profile" element={<PortalProfilePage />} />
        <Route path="orders" element={<OrdersListPage />} />
        <Route path="orders/new" element={<OrderFormPage />} />
        <Route path="orders/cabinets/new" element={<CabinetOrderPage />} />
        <Route path="orders/cabinets/:id" element={<CabinetOrderPage />} />
        <Route path="orders/:id" element={<OrderDetailPage />} />
        <Route path="orders/:id/edit" element={<OrderFormPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              <Route path="/login"  element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/access-denied" element={<AccessDeniedPage />} />
              <Route
                path="/pending"
                element={
                  <RequireAuth>
                    <PendingApprovalPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/staff/*"
                element={
                  <RequireAuth>
                    <RequireRole allow={STAFF_ONLY}>
                      <StaffRoutes />
                    </RequireRole>
                  </RequireAuth>
                }
              />
              <Route
                path="/portal/*"
                element={
                  <RequireAuth>
                    <RequireRole allow={CUSTOMER_ROLES}>
                      <PortalRoutes />
                    </RequireRole>
                  </RequireAuth>
                }
              />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <RoleRouter />
                  </RequireAuth>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
